import React, { useCallback, useEffect, useState } from 'react';
import { Switch, Route, useLocation, useHistory, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Feedback from './Feedback/Feedback';
import TicketDetailsPage from './Support/TicketDetailsPage';
import SupportCenter from './Support/SupportCenter';
import UserManual from './UserManual/UserManual';
import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import { PiChatTeardropTextLight, PiChatTeardropDotsLight, PiBookOpenTextLight, PiPlusLight } from 'react-icons/pi';
import { useDebounce } from 'use-debounce';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, helpCenterTourSteps } from '../shared/FeatureTour';

interface TabItem {
  label: string;
  value: string;
  description: string;
  icon?: React.ReactElement;
}

const HelpCenter: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const isTicketDetails = location.pathname.startsWith('/help/support/') && location.pathname !== '/help/support';
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortValue, setSortValue] = useState('created_at_desc');

  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'help-center',
    steps: helpCenterTourSteps,
    autoStartDelay: 600,
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setIsCreatingTicket(params.get('add') === 'true');
  }, [location.search]);

  const tabs: TabItem[] = [
    {
      label: t('User manual'),
      value: '/help/user-manual',
      description: t('Get assistance by creating or viewing support tickets.'),
      icon: <PiBookOpenTextLight className="h-4 w-4" />,
    },
    {
      label: t('Support'),
      value: '/help/support',
      description: t('Get assistance by creating or viewing support tickets.'),
      icon: <PiChatTeardropTextLight className="h-4 w-4" />,
    },
    {
      label: t('Feedback'),
      value: '/help/feedback',
      description: t('Share your thoughts to help us improve your experience.'),
      icon: <PiChatTeardropDotsLight className="h-4 w-4" />,
    },
  ];

  const supportSortOptions = [
    { value: 'support_id_desc', label: t('Ticket ID'), current: sortValue === 'support_id_desc' },
    { value: 'status_desc', label: t('Status'), current: sortValue === 'status_desc' },
    { value: 'created_at_desc', label: t('Created at'), current: sortValue === 'created_at_desc' },
  ];

  const activeTab = tabs.find(tab => location.pathname.startsWith(tab.value))?.value || '/help/support';

  const handleTabClick = (tabValue: string) => history.push(tabValue);

  const actions = [
    {
      label: t('Create support ticket'),
      icon: <PiPlusLight className='w-4 h-4' />,
      onClick: () => history.push('/help/support?add=true'),
      variant: 'primary' as const,
    },
  ];

  return (
    <div className="flex flex-col bg-background-light dark:bg-background-dark transition-colors duration-300 h-screen overflow-hidden">
      {!isCreatingTicket && !isTicketDetails && (
        <>
          <Header actions={actions} />
          <div data-tour-id="help-tabs">
          <FilterBarWithTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={handleTabClick}
            // sortOptions={activeTab === '/help/support' ? supportSortOptions : []}
            // onSortChange={setSortValue}
            // onSearchChange={activeTab === '/help/support' ? handleSearchChange : undefined}
          />
          </div>
        </>
      )}

      <div className="flex-grow animate-fade-in overflow-hidden">
        <Switch>
          <Route path="/help/user-manual/:eyebrowSlug?/:subpageSlug?" component={UserManual} />
          <Route path="/help/feedback" component={Feedback} exact />
          <Route path="/help/support/:ticketId" component={TicketDetailsPage} exact />
          <Route
            path="/help/support"
            render={() => (
              <SupportCenter
                searchQuery={debouncedSearchQuery}
                sortValue={sortValue}
                isCreatingTicket={isCreatingTicket}
              />
            )}
            exact
          />
          <Redirect from="/help" to="/help/user-manual" exact />
        </Switch>
      </div>
      <FeatureTour
        steps={helpCenterTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default HelpCenter;